import { Injectable } from '@angular/core';
import { PreferenzeApp } from 'src/app/common/models/Preferenze';
import { Subject, BehaviorSubject, Observable, Subscription, lastValueFrom } from 'rxjs';
import { first } from 'rxjs/operators';
import { MemorizzatoreEntitaService } from '../memorizzatoreEntita/memorizzatore-entita.service';
import { Storage } from '@ionic/storage';
import { Setup } from 'src/app/utils/Setup';

/**
 * Service che espone tutti i metodi di interazione con l'unica
 * istanza di PreferenzeApp che è un dizionario che governa aspetto
 * e comportamento dei diversi componenti dell'app
 * 
 * Le preferenze vengono ottenute:
 * - nei componenti tramite sottoscrizione
 * - nei service tramite getter al momento dell'operazione
 * 
 * Le preferenze vengono scritte:
 * - Dopo l'ultimo login via backend
 * - Dalla pagina delle impostazioni tramite singoli metodi
 * che agiscono singolarmente sulle opzioni
 */
@Injectable({
  providedIn: 'root'
})
export class PreferencesService extends MemorizzatoreEntitaService<PreferenzeApp> {
  /**
   * Emettitore dell'ultimo insieme delle preferenze utente a cui i diversi componenti
   * dell'applicazione si DEVONO iscrivere per avere il valore (aggiornato)
   */
  private preferenzeUtenteObservable: Subject<PreferenzeApp>;
  private STORAGE_KEY_CONFIGURAZIONE_UTENTE = '__preferenzeUtente';

  private lastPrefState: PreferenzeApp;
  private internalSubscriptions: Subscription;

  protected getChiaveDB() {
    return this.STORAGE_KEY_CONFIGURAZIONE_UTENTE;
  }

  protected verificaUguaglianzaEntita(a: PreferenzeApp, b: PreferenzeApp) {
    return true; // sovrascrivere sempre
  }

  constructor(storage: Storage) {
    super(storage);
    this.initializeObservables();
  }

  private async initializeObservables() {
    if (!this.preferenzeUtenteObservable) {
      let userPrefs = new PreferenzeApp(); // Creo da zero e copio i vecchi valori eventualmente letti
      const preferenzeaDb = await this.getElencoEntita();
      if (!(preferenzeaDb && preferenzeaDb.length > 0)) {
        console.warn('Nessuna preferenza memorizzato precedentemente');
      } else {
        userPrefs = Object.assign(userPrefs, preferenzeaDb[0]);
      }
      this.preferenzeUtenteObservable = new BehaviorSubject<PreferenzeApp>(userPrefs);
      console.debug('Inizializzato observable preferenze utente');
      this.internalSubscriptions = this.preferenzeUtenteObservable.subscribe(pref => {
        this.lastPrefState = pref;
      });
    }
  }

  /**
   * Metodo che memorizza i dati delle preferenze utente;
   * invocato: 
   * - In seguito ad un cambio di una singola preferenza da
   * questo provider
   * - Dopo il login
   * 
   * Come effetto collaterale una copia (per difendersi da modifiche
   * non veicolate dai setter forniti) dello stato viene trasmessa
   * ai sottoscriventi
   * @param newPrefs 
   */
  private async setPreferenze(newPrefs: PreferenzeApp) {
    await this.salvaEntita(newPrefs, true);
    if (!this.preferenzeUtenteObservable) {
      await this.initializeObservables();
    } else {
      this.preferenzeUtenteObservable.next(JSON.parse(JSON.stringify(newPrefs)));
    }
  }

  /**
   * Ottenimento del Observable da cui leggere e ricevere
   * gli aggiornamenti delle preferenze utente.
   * Al primo uso dell'app viene ritornato un oggetto con valori di default
   * 
   * Asincrono solo alla prima invocazione (pattern singleton: recupero
   * l'unica istanza dal DB solo all'inizializzazione dell'app)
   */
  async getPreferenzeUtenteObservable(): Promise<Observable<PreferenzeApp>> {
    if (!this.preferenzeUtenteObservable) {
      await this.initializeObservables();
    }
    return this.preferenzeUtenteObservable;
  }

  /**
   * Ritorna l'ultimo stato delle preferenzeUtente disponibile
   * One shot, consente al chiamante di scaricare il dato aggiornato ad un certo
   * momento senza iscriversi
   * MEGLIO USARE la sottoscrizione all'observable
   * Al primo uso dell'app viene ritornato un oggetto con valori di default
   */
  async getPreferenzeUtente(): Promise<PreferenzeApp> {
    await this.getPreferenzeUtenteObservable();
    return lastValueFrom(this.preferenzeUtenteObservable.pipe(first()));
  }

  /**
   * Ad ogni login/aggiornamento dei dati dell'utente dal web service
   * viene ritornata una configurazione che governa alcune funzionalita'.
   * Questo metodo incorpora tutte le personalizzazioni supportate nelle
   * preferenzee applicative correntemente disponibili
   */
  async settaConfigurazioneDaLogin(configurazione: Setup[]) {
    /**
     * Ottengo le preferenze precedente ed eventualmente aggiorno
     * quel che e' cambiato da configurazione backend (cioe' senza
     * intaccare i valori cambiati in app [lingua, darkmode, etc])
     */
    const preferenze = await this.getPreferenzeUtente();
    preferenze.SIGLA_CLIENTE = (configurazione.find(c => c.chiave === 'siglaclie')?.valore ?? '');
    preferenze.VERSIONE_INTEGRA = (configurazione.find(c => c.chiave === 'versione')?.valore ?? '');
    preferenze.MOSTRA_RICAMBI = (configurazione.find(c => c.chiave === 'visricambi')?.valore === 'TRUE');
    preferenze.MOLTEPLICITA_MINUTI_CONSENTITA = (Number(configurazione.find(c => c.chiave === 'mulminuti')?.valore) ?? 1);
    preferenze.DISABILITA_AGGIUNGI_INTERVENTO = (configurazione.find(c => c.chiave === 'bloccainsintervento')?.valore === 'TRUE');
    preferenze.ORDINE_DECRESCENTE_INTERVENTI = true;
    preferenze.LEGGI_ATTIVITA_DA_COMMESSA = preferenze.SIGLA_CLIENTE == 'NOV'; // FIXME: con operatore diverso
    preferenze.COORDINATE_FIRMA = {
      x: +(configurazione.find(c => c.chiave === 'posFirmaX')?.valore ?? 450),
      y: +(configurazione.find(c => c.chiave === 'posFirmaY')?.valore ?? 5)
    };
    preferenze.MOSTRA_SPESE = (configurazione.find(c => c.chiave === 'visSpese')?.valore ?? 'N');
    preferenze.MAIL_OBB = (configurazione.find(c => c.chiave === 'MAILOBB')?.valore === 'TRUE');
    preferenze.PRESELEZIONA_DESTINATARI_MAIL = (configurazione.find(c => c.chiave === 'PRESELDESTMAIL')?.valore === 'TRUE');
    preferenze.ABILITA_COLLASSO_NOTE = (configurazione.find(c => c.chiave === 'ABILITACOLLASSONOTE')?.valore ?? '0');
    preferenze.DESCRPROMOD = (configurazione.find(c => c.chiave === 'DESCRPROMOD')?.valore ?? '0');
    preferenze.PREZZOMOD = (configurazione.find(c => c.chiave === 'PREZZOMOD')?.valore ?? '0');
    preferenze.NUMDECPREZZOMOD = Number(configurazione.find(c => c.chiave === 'NUMDECPREZZOMOD')?.valore ?? 2);

    preferenze.FLTRSTINT = configurazione
    .filter(c => /^FLTRSTINT\d+$/.test(c.chiave))
    .sort((a, b) => {
      const numeroA = Number(a.chiave.replace('FLTRSTINT', ''));
      const numeroB = Number(b.chiave.replace('FLTRSTINT', ''));
      return numeroA - numeroB;
    })
    .map(c => c.valore ?? '')
    .filter(valore => valore !== '');

  preferenze.MTOMVSWKF = configurazione
    .filter(c => c.chiave === 'MTOMVSWKF')
    .map(c => c.valore ?? '')
    .filter(valore => valore !== '');
  
  preferenze.MOTIVOKO = configurazione
    .filter(c => /^MOTIVOKO\d+$/.test(c.chiave))
    .sort((a, b) => {
      const numeroA = Number(a.chiave.replace('MOTIVOKO', ''));
      const numeroB = Number(b.chiave.replace('MOTIVOKO', ''));
      return numeroA - numeroB;
    })
    .map(c => c.valore ?? '')
    .filter(valore => valore !== '');

    preferenze.MODCNTINT = (configurazione.find(c => c.chiave === 'MODCNTINT')?.valore ?? '0');

    // Preferenze specifiche di app
    await this.setPreferenze(preferenze);
  }

  async deletePreferencesAfterLogout() {
    await this.rimuoviElencoEntita();
  }

  async settaPreferenzaModalitaScura(usaTemaScuro: boolean) {
    const preferenzeUtente = await this.getPreferenzeUtente();
    preferenzeUtente.MODALITA_SCURA = usaTemaScuro;
    await this.setPreferenze(preferenzeUtente);
  }

  async settaPreferenzaSorgenteAnagrafiche(valore: boolean) {
    const preferenzeUtente = await this.getPreferenzeUtente();
    preferenzeUtente.USA_SOLO_ANAGRAFICHE_OFFLINE = valore;
    await this.setPreferenze(preferenzeUtente);
  }

  async settaPreferenzaOrdinamentoInterventi(valore: boolean) {
    const preferenzeUtente = await this.getPreferenzeUtente();
    preferenzeUtente.ORDINE_DECRESCENTE_INTERVENTI = valore;
    await this.setPreferenze(preferenzeUtente);
  }


}



